import { useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, ArrowUpDown, Columns3, Copy, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { copyText } from './CopyLink';

/**
 * The shared table (F7). Sort and pagination are driven by the screen, so they
 * can be done by the server (W2 paginated routes). The table owns the four
 * states of DESIGN.md §4 (DES-12):
 *   loading  → skeleton rows
 *   error    → message, code, "Réessayer"
 *   empty    → one CTA (nothing exists yet)
 *   filtered → "Aucun résultat" + clear the filters (data exists, the filters hide it)
 *   partial  → "N affichés sur T" and why, above the rows (some rows could not be shown)
 * Scope is kept narrow on purpose: sort, hideable columns, pagination,
 * filter pills, states. Nothing else.
 */
export interface DataTableColumn<Row> {
  id: string;
  header: ReactNode;
  cell: (row: Row) => ReactNode;
  sortable?: boolean;
  hideable?: boolean;
  defaultHidden?: boolean;
  align?: 'left' | 'right';
  /** QA-W7-12: keep the column in view while a wide table scrolls (e.g. the row menu). */
  sticky?: 'left' | 'right';
}

/** Classes and background of a sticky column's cells (QA-W7-12). */
function stickyCell(side: 'left' | 'right' | undefined, background: string): { className: string; style: CSSProperties } {
  if (!side) return { className: '', style: {} };
  return { className: `sticky ${side === 'right' ? 'right-0' : 'left-0'} z-[1]`, style: { background } };
}

export interface SortState { id: string; dir: 'asc' | 'desc' }

export interface FilterPill { id: string; label: ReactNode; onRemove: () => void }

interface Props<Row> {
  columns: DataTableColumn<Row>[];
  rows: Row[];
  getRowId: (row: Row) => string;
  status: 'loading' | 'error' | 'ready';
  /** DES-16: translated message, then code and correlation id with a copy button. */
  error?: { message: string; code?: string; correlationId?: string; onRetry: () => void };
  sort?: SortState | null;
  onSortChange?: (sort: SortState | null) => void;
  filters?: FilterPill[];
  onClearFilters?: () => void;
  empty: { title: ReactNode; action?: ReactNode };
  pagination?: { page: number; pageSize: number; total: number | null; onPageChange: (page: number) => void };
  onRowClick?: (row: Row) => void;
  caption: string;
  /** Some rows could not be shown (unreadable, over a cap): said above the table, never silent. */
  partial?: { shown: number; total: number; reason?: ReactNode };
  /** Remembers hidden columns per browser (convenience only). */
  storageKey?: string;
}

function readHidden(storageKey: string | undefined, fallback: string[]): string[] {
  if (!storageKey) return fallback;
  try {
    const raw = localStorage.getItem(`datatable:${storageKey}:hidden`);
    if (!raw) return fallback;
    // A value written by another version (or by hand) must not break the table.
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : fallback;
  } catch {
    return fallback;
  }
}

export function DataTable<Row>(props: Props<Row>) {
  const { columns, rows, getRowId, status, error, sort, onSortChange, filters = [], onClearFilters, empty, pagination, onRowClick, caption, storageKey, partial } = props;
  const { t } = useTranslation(['common']);
  const [hidden, setHidden] = useState<string[]>(() => readHidden(storageKey, columns.filter((c) => c.defaultHidden).map((c) => c.id)));
  const [menuOpen, setMenuOpen] = useState(false);
  const visible = useMemo(() => columns.filter((c) => !hidden.includes(c.id)), [columns, hidden]);

  const toggleColumn = (id: string) => {
    const next = hidden.includes(id) ? hidden.filter((h) => h !== id) : [...hidden, id];
    setHidden(next);
    if (storageKey) try { localStorage.setItem(`datatable:${storageKey}:hidden`, JSON.stringify(next)); } catch { /* storage unavailable */ }
  };

  const cycleSort = (id: string) => {
    if (!onSortChange) return;
    if (!sort || sort.id !== id) onSortChange({ id, dir: 'asc' });
    else if (sort.dir === 'asc') onSortChange({ id, dir: 'desc' });
    else onSortChange(null);
  };

  const filtered = filters.length > 0;
  // A page past the end (total unknown, or rows deleted meanwhile) keeps
  // « Précédent »: the user is never stranded on an empty page.
  const emptyPage = !!pagination && pagination.page > 0 && rows.length === 0;
  const pageCount = pagination && pagination.total !== null ? Math.max(1, Math.ceil(pagination.total / pagination.pageSize)) : null;

  return (
    <div className="space-y-2">
      {(filters.length > 0 || columns.some((c) => c.hideable)) && (
        <div className="flex flex-wrap items-center gap-2">
          {filters.map((f) => (
            <span key={f.id} className="inline-flex items-center gap-1 rounded-full border px-2.5 h-7 text-[12px]" style={{ borderColor: 'var(--vf-accent-bd)', background: 'var(--vf-accent-bg)', color: 'var(--vf-text-1)' }}>
              {f.label}
              <button type="button" onClick={f.onRemove} aria-label={t('common:dataTable.removeFilter')} className="-mr-1.5 inline-flex h-8 w-8 items-center justify-center rounded-full hover:bg-[var(--vf-surface-3)]">
                <X className="h-3 w-3" aria-hidden />
              </button>
            </span>
          ))}
          {filters.length > 1 && onClearFilters && (
            <Button type="button" variant="ghost" size="sm" onClick={onClearFilters}>{t('common:dataTable.clearFilters')}</Button>
          )}
          {columns.some((c) => c.hideable) && (
            <div className="relative ml-auto">
              <Button type="button" variant="outline" size="sm" aria-expanded={menuOpen} onClick={() => setMenuOpen((o) => !o)}>
                <Columns3 className="mr-1.5 h-4 w-4" aria-hidden />{t('common:dataTable.columns')}
              </Button>
              {menuOpen && (
                <div role="group" aria-label={t('common:dataTable.columns')} className="absolute right-0 z-20 mt-1 min-w-48 rounded-vf border p-2 shadow-vf-md" style={{ background: 'var(--vf-bg)', borderColor: 'var(--vf-border)' }}>
                  {columns.filter((c) => c.hideable).map((c) => (
                    <label key={c.id} className="flex items-center gap-2 rounded px-2 py-1.5 text-[13px] hover:bg-[var(--vf-surface-2)]">
                      <input type="checkbox" checked={!hidden.includes(c.id)} onChange={() => toggleColumn(c.id)} />
                      {c.header}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {status === 'ready' && partial && partial.shown < partial.total && (
        <p role="status" className="rounded-vf border px-3 py-2 text-[12px]" style={{ borderColor: 'var(--vf-border)', background: 'var(--vf-surface-1)', color: 'var(--vf-text-2)' }}>
          <span className="tabular-nums font-medium" style={{ color: 'var(--vf-text-1)' }}>{t('common:dataTable.partial', { shown: partial.shown, total: partial.total })}</span>
          {partial.reason && <> · {partial.reason}</>}
        </p>
      )}

      {/* QA-W5-3: positioned, so the absolute (sr-only) bits of a wide table are clipped here instead of widening the page. */}
      <div className="relative overflow-x-auto rounded-vf border" data-testid="datatable-scroll" style={{ borderColor: 'var(--vf-border)' }}>
        <table className="w-full text-[13px]">
          <caption className="sr-only">{caption}</caption>
          <thead style={{ background: 'var(--vf-surface-1)' }}>
            <tr>
              {visible.map((c) => {
                const active = sort?.id === c.id ? sort.dir : null;
                return (
                  <th
                    key={c.id}
                    scope="col"
                    aria-sort={active === 'asc' ? 'ascending' : active === 'desc' ? 'descending' : c.sortable ? 'none' : undefined}
                    className={`px-3 h-10 font-semibold text-[11px] uppercase tracking-wide ${c.align === 'right' ? 'text-right' : 'text-left'} ${stickyCell(c.sticky, 'var(--vf-surface-1)').className}`}
                    style={{ color: 'var(--vf-text-3)', ...stickyCell(c.sticky, 'var(--vf-surface-1)').style }}
                  >
                    {c.sortable && onSortChange ? (
                      <button type="button" onClick={() => cycleSort(c.id)} className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-[var(--vf-text-1)]">
                        {c.header}
                        {active === 'asc' ? <ArrowUp className="h-3 w-3" aria-hidden /> : active === 'desc' ? <ArrowDown className="h-3 w-3" aria-hidden /> : <ArrowUpDown className="h-3 w-3 opacity-50" aria-hidden />}
                      </button>
                    ) : c.header}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {status === 'loading' && Array.from({ length: 5 }).map((_, i) => (
              <tr key={`sk-${i}`} data-testid="datatable-skeleton" className="border-t" style={{ borderColor: 'var(--vf-border)' }}>
                {visible.map((c) => (
                  <td key={c.id} className="px-3 h-11"><div className="h-3 w-3/4 animate-pulse rounded motion-reduce:animate-none" style={{ background: 'var(--vf-surface-3)' }} /></td>
                ))}
              </tr>
            ))}
            {status === 'ready' && rows.map((row) => (
              <tr
                key={getRowId(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                // A clickable row works from the keyboard too (Tab, then Enter or Space).
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={onRowClick ? (e) => {
                  if (e.target !== e.currentTarget) return;
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(row); }
                } : undefined}
                className={`border-t ${onRowClick ? 'cursor-pointer hover:bg-[var(--vf-surface-1)] focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--vf-accent)]' : ''}`}
                style={{ borderColor: 'var(--vf-border)' }}
              >
                {visible.map((c) => (
                  <td key={c.id} className={`px-3 h-11 ${c.align === 'right' ? 'text-right tabular-nums' : ''} ${stickyCell(c.sticky, 'var(--vf-bg)').className}`} style={{ color: 'var(--vf-text-1)', ...stickyCell(c.sticky, 'var(--vf-bg)').style }}>{c.cell(row)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {status === 'error' && error && (
          <div role="alert" className="flex flex-col items-center gap-2 px-6 py-10 text-center">
            <p className="text-[14px]" style={{ color: 'var(--vf-text-1)' }}>{error.message}</p>
            {(error.code || error.correlationId) && (
              <p className="flex items-center gap-2 font-mono text-[11px]" style={{ color: 'var(--vf-text-3)' }}>
                <span>{[error.code, error.correlationId].filter(Boolean).join(' · ')}</span>
                <button type="button" className="inline-flex h-8 w-8 items-center justify-center rounded hover:bg-[var(--vf-surface-3)]" aria-label={t('common:dataTable.copyCode')} onClick={() => void copyText([error.code, error.correlationId].filter(Boolean).join(' '))}>
                  <Copy className="h-3.5 w-3.5" aria-hidden />
                </button>
              </p>
            )}
            <Button type="button" variant="outline" size="sm" onClick={error.onRetry}>{t('common:actions.retry')}</Button>
          </div>
        )}
        {status === 'ready' && rows.length === 0 && (
          <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
            {emptyPage ? (
              <p className="text-[14px]" style={{ color: 'var(--vf-text-1)' }}>{t('common:dataTable.emptyPage')}</p>
            ) : filtered ? (
              <>
                <p className="text-[14px]" style={{ color: 'var(--vf-text-1)' }}>{t('common:dataTable.noResults')}</p>
                {onClearFilters && <Button type="button" variant="outline" size="sm" onClick={onClearFilters}>{t('common:dataTable.clearFilters')}</Button>}
              </>
            ) : (
              <>
                <p className="text-[14px]" style={{ color: 'var(--vf-text-1)' }}>{empty.title}</p>
                {empty.action}
              </>
            )}
          </div>
        )}
      </div>

      {pagination && status === 'ready' && (rows.length > 0 || emptyPage) && (
        <div className="flex items-center justify-between gap-2 text-[12px]" style={{ color: 'var(--vf-text-3)' }}>
          <span className="tabular-nums">
            {pagination.total !== null && rows.length > 0
              ? t('common:dataTable.range', {
                  from: pagination.page * pagination.pageSize + 1,
                  to: pagination.page * pagination.pageSize + rows.length,
                  total: pagination.total,
                })
              : null}
          </span>
          <span className="flex gap-2">
            <Button type="button" variant="outline" size="sm" disabled={pagination.page === 0} onClick={() => pagination.onPageChange(pagination.page - 1)}>{t('common:actions.previous')}</Button>
            <Button type="button" variant="outline" size="sm" disabled={pageCount !== null ? pagination.page + 1 >= pageCount : rows.length < pagination.pageSize} onClick={() => pagination.onPageChange(pagination.page + 1)}>{t('common:actions.next')}</Button>
          </span>
        </div>
      )}
    </div>
  );
}
